/** Energy summary, targets and nutrient panels shared by Dashboard and Diary. */
import { useState, type ReactElement } from 'react';
import { ENERGY_MODE_LABEL, type DayBurn } from '../lib/energy';
import { errorMessage } from '../lib/logger';
import { macroEnergySplit, nutrientTargets, type NutrientTarget } from '../lib/nutrition';
import { ENERGY_MODES, type EnergyMode, type MealNutrients, type Profile } from '../lib/types';
import { useApp } from '../state/app';
import { Card, Ring, TargetBar } from './ui';

const fmt = (n: number): string => Math.round(n).toLocaleString();

/** How the burn is worked out, switchable in place (the same setting as Targets & Profile and the phone). */
export function BurnModeSwitch(): ReactElement {
  const { settings, saveSettings, toast, bump } = useApp();
  const [busy, setBusy] = useState<boolean>(false);
  const change = async (mode: EnergyMode): Promise<void> => {
    if (mode === settings.energy_mode) return;
    setBusy(true);
    try {
      await saveSettings({ energy_mode: mode });
      toast(`Burn: ${ENERGY_MODE_LABEL[mode]}`);
      bump();
    } catch (err) {
      toast(errorMessage(err), 'err');
    } finally {
      setBusy(false);
    }
  };
  return (
    <label className="burn-switch" title="How your daily burn is worked out. More options in Targets & Profile.">
      <span className="faint">Burn</span>
      <select className="input input-sm" value={settings.energy_mode} disabled={busy} onChange={(e) => void change(e.target.value as EnergyMode)} aria-label="How your burn is worked out">
        {ENERGY_MODES.map((m) => <option key={m} value={m}>{ENERGY_MODE_LABEL[m]}</option>)}
      </select>
    </label>
  );
}

/** "3,680 kcal = 2,450 resting + 780 active + 200 walking (5,400 steps no app counted) + 250 digestion", or how else the burn was found. */
export function BurnBreakdown({ burn }: { burn: DayBurn }): ReactElement {
  if (burn.kcal === null) return <span className="faint">{burn.note ?? '—'}</span>;
  const total = <b className="num">{fmt(burn.kcal)} kcal</b>;
  let how: ReactElement;
  if (burn.method === 'wearable' && burn.basis === 'active') {
    how = (
      <>{' = '}<span className="num">{fmt(burn.bmrKcal ?? 0)}</span> resting
        {burn.activeKcal !== null && <> + <span className="num">{fmt(burn.activeKcal)}</span> active</>}
        {burn.walkingKcal !== null && burn.walkingKcal > 0 && <> + <span className="num">{fmt(burn.walkingKcal)}</span> walking ({fmt(burn.walkingSteps ?? 0)} steps no app counted)</>}
        {burn.partial ? ' so far' : ''}
        {burn.tefKcal !== null && burn.tefKcal > 0 && <> + <span className="num">{fmt(burn.tefKcal)}</span> digestion</>}</>
    );
  } else if (burn.method === 'wearable') {
    how = (
      <>{' = '}<span className="num">{fmt(burn.totalKcal ?? 0)}</span> from your watch{burn.partial ? ' so far' : ''}
        {burn.tefKcal !== null && burn.tefKcal > 0 && <> + <span className="num">{fmt(burn.tefKcal)}</span> digestion</>}</>
    );
  } else if (burn.method === 'activity') {
    how = <>{' = '}<span className="num">{fmt(burn.bmrKcal ?? 0)}</span> resting × {burn.pal} activity level</>;
  } else {
    how = <> learned from your weight trend</>;
  }
  return <span>{total}<span className="muted">{how}</span>{burn.note && <span className="faint" style={{ display: 'block', fontSize: 12.5, fontWeight: 600 }}>{burn.note}</span>}</span>;
}

const METHOD_TEXT: Readonly<Record<DayBurn['method'], string>> = {
  wearable: 'from your watch', activity: 'from your activity level', observed: 'learned from your weight', none: '',
};

export function EnergySummary({ totals, profile, burn }: { totals: MealNutrients; profile: Profile; burn: DayBurn }): ReactElement {
  const split = macroEnergySplit(totals);
  const burned: number | null = burn.kcal;
  const target: number | null = profile.target_calories;
  const remaining: number | null = target !== null ? target - totals.calories : null;
  const balance: number | null = burned !== null ? totals.calories - burned : null;

  return (
    <Card title="Energy summary" sub="calories" right={<BurnModeSwitch />}>
      <div className="rings3">
        <div className="ring-wrap">
          <Ring size={124} thickness={13} max={Math.max(totals.calories, 1)} title="Eaten, split by protein, carbs and fat"
            segments={[
              { value: split.protein * totals.calories, color: 'var(--protein)' },
              { value: split.carbs * totals.calories, color: 'var(--carbs)' },
              { value: split.fat * totals.calories, color: 'var(--fat)' },
              { value: split.alcohol * totals.calories, color: 'var(--alcohol)' },
            ]}>
            <div><div className="ring-center num" style={{ fontSize: 22 }}>{fmt(totals.calories)}</div><div className="faint" style={{ fontSize: 11, fontWeight: 700 }}>eaten</div></div>
          </Ring>
          <div className="ring-caption">Eaten</div>
        </div>
        <div className="ring-wrap">
          <Ring size={124} thickness={13} max={1} segments={[{ value: burned !== null ? 1 : 0, color: 'var(--burn)' }]} title="Burned">
            <div><div className="ring-center num" style={{ fontSize: 22 }}>{burned !== null ? fmt(burned) : '—'}</div><div className="faint" style={{ fontSize: 11, fontWeight: 700 }}>{burn.partial ? 'burned so far' : 'burned'}</div></div>
          </Ring>
          <div className="ring-caption">Burned</div>
        </div>
        <div className="ring-wrap">
          <Ring size={124} thickness={13} max={target ?? 1} title="Of your daily target"
            segments={[{ value: Math.min(totals.calories, target ?? 0), color: remaining !== null && remaining < 0 ? 'var(--bad)' : 'var(--energy)' }]}>
            <div>
              <div className="ring-center num" style={{ fontSize: 22, color: remaining !== null && remaining < 0 ? 'var(--bad)' : undefined }}>{remaining !== null ? fmt(Math.abs(remaining)) : '—'}</div>
              <div className="faint" style={{ fontSize: 11, fontWeight: 700 }}>{remaining === null ? 'no target' : remaining >= 0 ? 'left' : 'over'}</div>
            </div>
          </Ring>
          <div className="ring-caption">Target {target !== null ? fmt(target) : ''}</div>
        </div>
      </div>
      <div className="grid cols-2" style={{ marginTop: 18, gap: 18 }}>
        <div className="legend">
          <div className="legend-row"><span className="dot" style={{ background: 'var(--protein)' }} />Protein<span className="val num">{Math.round(totals.protein_g)} g · {Math.round(split.protein * 100)}%</span></div>
          <div className="legend-row"><span className="dot" style={{ background: 'var(--carbs)' }} />Carbs<span className="val num">{Math.round(totals.carbs_g)} g · {Math.round(split.carbs * 100)}%</span></div>
          <div className="legend-row"><span className="dot" style={{ background: 'var(--fat)' }} />Fat<span className="val num">{Math.round(totals.fat_g)} g · {Math.round(split.fat * 100)}%</span></div>
          {split.alcohol > 0 && <div className="legend-row"><span className="dot" style={{ background: 'var(--alcohol)' }} />Alcohol<span className="val num">{Math.round(split.alcohol * 100)}%</span></div>}
        </div>
        <div className="legend">
          {burn.bmrKcal !== null && <div className="legend-row"><span className="dot" style={{ background: 'var(--burn)' }} />Resting (BMR)<span className="val num">{fmt(burn.bmrKcal)}</span></div>}
          {burn.method === 'wearable' && burn.basis === 'active' && burn.activeKcal !== null && <div className="legend-row"><span className="dot" style={{ background: 'var(--carbs)' }} />Active (watch){burn.partial ? ', so far' : ''}<span className="val num">{fmt(burn.activeKcal)}</span></div>}
          {burn.walkingKcal !== null && burn.walkingKcal > 0 && <div className="legend-row"><span className="dot" style={{ background: 'var(--carbs)' }} />Walking no app counted{burn.partial ? ', so far' : ''}<span className="val num">{fmt(burn.walkingKcal)}</span></div>}
          {burn.method === 'wearable' && burn.basis === 'total' && <div className="legend-row"><span className="dot" style={{ background: 'var(--carbs)' }} />Watch total{burn.partial ? ', so far' : ''}<span className="val num">{fmt(burn.totalKcal ?? 0)}</span></div>}
          {burn.tefKcal !== null && burn.tefKcal > 0 && <div className="legend-row"><span className="dot" style={{ background: 'var(--energy)' }} />Digestion<span className="val num">{fmt(burn.tefKcal)}</span></div>}
          <div className="legend-row"><span className="dot" style={{ background: 'var(--text-3)' }} />Balance<span className={`val num ${balance === null ? '' : balance < 0 ? 'good' : 'warn'}`}>{balance === null ? '—' : `${balance > 0 ? '+' : ''}${fmt(balance)}`}</span></div>
          <div className="faint" style={{ fontSize: 12, fontWeight: 600 }}>{burned !== null ? `Burn ${METHOD_TEXT[burn.method]}.` : ''} {burn.note ?? ''}</div>
        </div>
      </div>
    </Card>
  );
}

export function TargetsPanel({ totals, profile }: { totals: MealNutrients; profile: Profile }): ReactElement {
  const extras: NutrientTarget[] = nutrientTargets(profile, profile.target_calories);
  return (
    <Card title="Targets" sub="today">
      <TargetBar name="Calories" value={totals.calories} target={profile.target_calories} unit="kcal" color="var(--energy)" />
      <TargetBar name="Protein" value={totals.protein_g} target={profile.target_protein_g} unit="g" color="var(--protein)" />
      <TargetBar name="Carbs" value={totals.carbs_g} target={profile.target_carbs_g} unit="g" color="var(--carbs)" />
      <TargetBar name="Fat" value={totals.fat_g} target={profile.target_fat_g} unit="g" color="var(--fat)" />
      <div style={{ height: 8 }} />
      {extras.map((t) => (
        <TargetBar key={t.key} name={t.label} value={(totals[t.key] ?? 0)} target={t.target} unit={t.unit} kind={t.kind}
          color={t.key === 'fiber_g' ? 'var(--carbs)' : t.key === 'potassium_mg' ? 'var(--burn)' : 'var(--warn)'} />
      ))}
      <p className="cite" style={{ marginTop: 12 }}>Fiber: IOM 2005 (14 g/1,000 kcal). Potassium & sodium: NASEM 2019. Sodium is a “stay under” limit.</p>
    </Card>
  );
}

export function HighlightRings({ totals, profile }: { totals: MealNutrients; profile: Profile }): ReactElement {
  const extras: NutrientTarget[] = nutrientTargets(profile, profile.target_calories);
  const items: Array<{ label: string; value: number; target: number | null; color: string; unit: string; kind: 'min' | 'max' }> = [
    { label: 'Protein', value: totals.protein_g, target: profile.target_protein_g, color: 'var(--protein)', unit: 'g', kind: 'min' },
    ...extras.map((t) => ({ label: t.label, value: totals[t.key] ?? 0, target: t.target, color: t.key === 'fiber_g' ? 'var(--carbs)' : t.key === 'potassium_mg' ? 'var(--burn)' : 'var(--warn)', unit: t.unit, kind: t.kind })),
  ];
  return (
    <Card title="Highlighted nutrients">
      <div className="rings4">
        {items.map((it) => {
          const pct: number = it.target ? it.value / it.target : 0;
          const over: boolean = it.kind === 'max' && pct > 1;
          return (
            <div key={it.label} className="ring-wrap">
              <Ring size={84} thickness={9} max={1} segments={[{ value: Math.min(1, pct), color: over ? 'var(--bad)' : it.color }]} title={it.label}>
                <span className="ring-center num" style={{ fontSize: 15, color: over ? 'var(--bad)' : undefined }}>{it.target ? `${Math.round(pct * 100)}%` : '—'}</span>
              </Ring>
              <div className="ring-caption">{it.label}</div>
              <div className="faint num" style={{ fontSize: 11.5, fontWeight: 600, marginTop: -4 }}>{Math.round(it.value).toLocaleString()} {it.unit}</div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
